import React from 'react';
import { usesTableMap } from '../../utils/roomMode.ts';
import { createPortal } from 'react-dom';
import { Copy, Download, X } from 'lucide-react';
import { formatPeopleLabel } from '../../utils/reservationPeople.ts';
import type { SalaOcupacao, WaitlistEntry } from '../../services/api.ts';

type CapacityWarningInfo = {
  title: string;
  message: string;
  confirmLabel: string;
};

type QrCodeModalState = {
  title: string;
  link: string;
  fileName: string;
};

type MesaSelectModalState = {
  reservaId?: string;
  waitlistEntry?: WaitlistEntry;
  guests: number;
  salaId: string | number;
  mode: 'seat' | 'select' | 'leave' | 'waitlist-seat';
};

type RoomSeatModalState = {
  source: 'reservation' | 'waitlist';
  sourceId: string;
  name: string;
  phone?: string | null;
  guests: number;
  salaId: string;
};

type SalaReleaseModalState = {
  ocupacao: SalaOcupacao;
  quantidadeSaida: number;
};

type NoShowConfirmationState = {
  reservationId: string;
  reservationName: string;
  reservationTime: string;
};

type ModalCapacitySummary = {
  label: string;
  value: string;
  detail?: string;
};

type FloorStats = {
  total: number;
  occupied: number;
  available: number;
};

type DashboardModalsProps = {
  qrCodeModal: QrCodeModalState | null;
  activeQrCodeDataUrl: string;
  setQrCodeModal: (value: QrCodeModalState | null) => void;
  handleCopyQrCode: () => void;
  handleSaveQrCode: () => void;

  showMesaSelectModal: MesaSelectModalState | null;
  closeMesaSelectModal: () => void;
  mesaModalSubmitting: boolean;
  mesaSelectModalIsLeaveMode: boolean;
  mesaSelectModalIsSeatMode: boolean;
  mesaSelectModalIsWaitlistSeatMode: boolean;
  mesaSelectModalAllowsMultiple: boolean;
  handleMesaSelectModalSubmit: (event: React.FormEvent) => void;
  modalFloorData: any | null;
  floors: any[];
  legacyMapDefault?: boolean;
  handleWaitlistSeatRoomChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  modalSelectionSummary: string;
  modalGuestCount: number;
  modalWaitlistEntry: WaitlistEntry | null;
  modalSelectedCapacitySummary: ModalCapacitySummary;
  modalCapacityWarning: CapacityWarningInfo | null;
  mesaCapacityOverflowConfirmed: boolean;
  setMesaCapacityOverflowConfirmed: (value: boolean) => void;
  modalTableGroups: Array<{ key: string; label: string; options: any[] }>;
  modalTableOptions: any[];
  renderModalTableOption: (option: any) => React.ReactNode;
  modalReservation: any | null;
  resolveTableDisplayNames: (ids: Array<string | number | null | undefined>) => string | null;
  modalCanSubmit: boolean;

  roomSeatModal: RoomSeatModalState | null;
  setRoomSeatModal: React.Dispatch<React.SetStateAction<RoomSeatModalState | null>>;
  roomSeatSubmitting: boolean;
  handleRoomSeatSubmit: (seatRequest: RoomSeatModalState) => void | Promise<void>;
  floorStats: Map<string, FloorStats>;

  salaReleaseModal: SalaReleaseModalState | null;
  setSalaReleaseModal: React.Dispatch<React.SetStateAction<SalaReleaseModalState | null>>;
  salaReleaseSubmitting: boolean;
  handleSalaReleaseSubmit: (event: React.FormEvent) => void;
  reservationById: Map<string, any>;

  noShowConfirmation: NoShowConfirmationState | null;
  setNoShowConfirmation: (value: NoShowConfirmationState | null) => void;
  statusUpdatingId: string | null;
  handleConfirmNoShow: () => void;
};

const DashboardModals: React.FC<DashboardModalsProps> = ({
  qrCodeModal,
  activeQrCodeDataUrl,
  setQrCodeModal,
  handleCopyQrCode,
  handleSaveQrCode,
  showMesaSelectModal,
  closeMesaSelectModal,
  mesaModalSubmitting,
  mesaSelectModalIsLeaveMode,
  mesaSelectModalIsSeatMode,
  mesaSelectModalIsWaitlistSeatMode,
  mesaSelectModalAllowsMultiple,
  handleMesaSelectModalSubmit,
  modalFloorData,
  floors,
  legacyMapDefault = true,
  handleWaitlistSeatRoomChange,
  modalSelectionSummary,
  modalGuestCount,
  modalWaitlistEntry,
  modalSelectedCapacitySummary,
  modalCapacityWarning,
  mesaCapacityOverflowConfirmed,
  setMesaCapacityOverflowConfirmed,
  modalTableGroups,
  modalTableOptions,
  renderModalTableOption,
  modalReservation,
  resolveTableDisplayNames,
  modalCanSubmit,
  roomSeatModal,
  setRoomSeatModal,
  roomSeatSubmitting,
  handleRoomSeatSubmit,
  floorStats,
  salaReleaseModal,
  setSalaReleaseModal,
  salaReleaseSubmitting,
  handleSalaReleaseSubmit,
  reservationById,
  noShowConfirmation,
  setNoShowConfirmation,
  statusUpdatingId,
  handleConfirmNoShow,
}) => {
  if (typeof document === 'undefined') {
    return null;
  }

  return (
    <>
      {qrCodeModal && activeQrCodeDataUrl && createPortal(
        <div
          className="reservation-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reservo-qr-modal-title"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setQrCodeModal(null);
            }
          }}
        >
          <div className="reservation-modal-content reservo-qr-modal">
            <button
              type="button"
              className="reservation-modal-close"
              onClick={() => setQrCodeModal(null)}
              aria-label="Fechar QR Code"
            >
              <X size={18} />
            </button>
            <h2 id="reservo-qr-modal-title" className="reservation-modal-title">
              {qrCodeModal.title}
            </h2>
            <div className="reservo-qr-preview">
              <img src={activeQrCodeDataUrl} alt={qrCodeModal.title} />
            </div>
            <a
              className="reservo-qr-link"
              href={qrCodeModal.link}
              target="_blank"
              rel="noreferrer"
            >
              {qrCodeModal.link}
            </a>
            <div className="reservo-qr-actions">
              <button
                type="button"
                className="reservo-qr-action-button"
                onClick={handleCopyQrCode}
              >
                <Copy size={16} />
                <span>Copiar QR</span>
              </button>
              <button
                type="button"
                className="reservo-qr-action-button"
                onClick={handleSaveQrCode}
              >
                <Download size={16} />
                <span>Salvar PNG</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showMesaSelectModal && createPortal(
        <div
          className="reservation-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="reservation-modal-title"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              closeMesaSelectModal();
            }
          }}
        >
          <div className="reservation-modal-content">
            <button
              type="button"
              className="reservation-modal-close"
              onClick={closeMesaSelectModal}
              aria-label="Fechar seleção de mesa"
              disabled={mesaModalSubmitting}
            >
              x
            </button>
            <h2 id="reservation-modal-title" className="reservation-modal-title">
              {mesaSelectModalIsLeaveMode
                ? 'Levantar Mesa'
                : (
                  mesaSelectModalIsSeatMode || mesaSelectModalIsWaitlistSeatMode
                    ? (mesaSelectModalIsWaitlistSeatMode ? 'Selecionar Mesa para Cliente da Fila' : 'Selecionar Mesas para Sentar')
                    : 'Selecionar Mesas'
                )}
            </h2>
            <form
              className="reservation-modal-form"
              onSubmit={handleMesaSelectModalSubmit}
            >
              {!modalFloorData && (
                <div className="room-seat-error">
                  Não foi possível identificar o ambiente desta reserva.
                </div>
              )}
              {mesaSelectModalIsWaitlistSeatMode && floors.length > 0 && (
                <div className="reservation-modal-room-select">
                  <label htmlFor="waitlist-seat-room">Ambiente</label>
                  <select
                    id="waitlist-seat-room"
                    className="form-control"
                    value={String(showMesaSelectModal.salaId || '')}
                    onChange={handleWaitlistSeatRoomChange}
                    disabled={mesaModalSubmitting}
                  >
                    {floors.map((floor) => (
                      <option key={String(floor.id)} value={String(floor.id)}>
                        {floor.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {mesaSelectModalAllowsMultiple && (
                <small className="reservation-modal-hint">
                  {modalSelectionSummary} Clique nas mesas para marcar ou desmarcar a seleção.
                </small>
              )}
              <div className="reservation-modal-summary">
                <div className="reservation-modal-summary-card">
                  <span className="reservation-modal-summary-label">
                    {mesaSelectModalIsWaitlistSeatMode ? 'Grupo da fila' : 'Grupo da reserva'}
                  </span>
                  <strong className="reservation-modal-summary-value">
                    {formatPeopleLabel(modalGuestCount)}
                  </strong>
                  <span className="reservation-modal-summary-detail">
                    {mesaSelectModalIsWaitlistSeatMode
                      ? (modalWaitlistEntry?.nome_cliente || 'Cliente da fila')
                      : 'Total informado na reserva.'}
                  </span>
                </div>
                <div className="reservation-modal-summary-card">
                  <span className="reservation-modal-summary-label">
                    {modalSelectedCapacitySummary.label}
                  </span>
                  <strong className="reservation-modal-summary-value">
                    {modalSelectedCapacitySummary.value}
                  </strong>
                  <span className="reservation-modal-summary-detail">
                    {modalSelectedCapacitySummary.detail}
                  </span>
                </div>
              </div>
              {modalCapacityWarning && (
                <div className="reservation-modal-warning" role="alert">
                  <strong className="reservation-modal-warning-title">
                    {modalCapacityWarning.title}
                  </strong>
                  <p className="reservation-modal-warning-text">
                    {modalCapacityWarning.message}
                  </p>
                  <label className="reservation-modal-warning-check">
                    <input
                      type="checkbox"
                      checked={mesaCapacityOverflowConfirmed}
                      onChange={(event) => setMesaCapacityOverflowConfirmed(event.target.checked)}
                      disabled={mesaModalSubmitting}
                    />
                    <span>{modalCapacityWarning.confirmLabel}</span>
                  </label>
                </div>
              )}
              {mesaSelectModalAllowsMultiple ? (
                <div
                  className="reservation-modal-table-groups"
                  role="group"
                  aria-label="Mesas disponíveis agrupadas por capacidade"
                >
                  {modalTableGroups.map((group) => (
                    <section key={group.key} className="reservation-modal-table-group">
                      <div className="reservation-modal-table-group-header">
                        <span className="reservation-modal-table-group-title">
                          {group.label}
                        </span>
                        <span className="reservation-modal-table-group-meta">
                          {group.options.length} mesa{group.options.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="reservation-modal-table-list">
                        {group.options.map((option) => renderModalTableOption(option))}
                      </div>
                    </section>
                  ))}
                </div>
              ) : (
                <div
                  className="reservation-modal-table-list reservation-modal-table-list-scrollable"
                  role="radiogroup"
                  aria-label="Mesas ativas da reserva"
                >
                  {modalTableOptions.map((option) => renderModalTableOption(option))}
                </div>
              )}
              {modalReservation?.tableIds?.length > 0 && (
                <small className="reservation-modal-hint">
                  Mesas vinculadas à reserva: {resolveTableDisplayNames(modalReservation.tableIds)}
                </small>
              )}

              <div className="reservo-button-inline">
                <button
                  className="action-button"
                  type="submit"
                  disabled={!modalCanSubmit}
                >
                  <span>
                    {mesaModalSubmitting
                      ? (
                        mesaSelectModalIsLeaveMode
                          ? 'Liberando...'
                          : ((mesaSelectModalIsSeatMode || mesaSelectModalIsWaitlistSeatMode) ? 'Sentando...' : 'Salvando...')
                      )
                      : (
                        mesaSelectModalIsLeaveMode
                          ? 'Levantar mesa'
                          : (mesaSelectModalIsWaitlistSeatMode ? 'Sentar cliente' : (mesaSelectModalIsSeatMode ? 'Sentar mesas' : 'Salvar seleção'))
                      )}
                  </span>
                </button>
                <button
                  className="action-button"
                  type="button"
                  onClick={closeMesaSelectModal}
                  disabled={mesaModalSubmitting}
                >
                  <span>Cancelar</span>
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {roomSeatModal && createPortal(
        <div
          className="reservation-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="room-seat-modal-title"
          onClick={(event) => {
            if (event.target === event.currentTarget && !roomSeatSubmitting) {
              setRoomSeatModal(null);
            }
          }}
        >
          <div className="reservation-modal-content reservation-modal-content-confirmation">
            <button
              type="button"
              className="reservation-modal-close"
              onClick={() => setRoomSeatModal(null)}
              aria-label="Fechar seleção de ambiente"
              disabled={roomSeatSubmitting}
            >
              ×
            </button>
            <h2 id="room-seat-modal-title" className="reservation-modal-title">
              Selecionar ambiente
            </h2>
            <form
              className="reservation-modal-form"
              onSubmit={(event) => {
                event.preventDefault();
                void handleRoomSeatSubmit(roomSeatModal);
              }}
            >
              <p className="reservation-modal-copy">
                Sentar <strong>{roomSeatModal.name}</strong>, com{' '}
                <strong>{formatPeopleLabel(roomSeatModal.guests)}</strong>.
              </p>
              <div className="reservation-modal-room-select">
                <label htmlFor="room-seat-room">Ambiente</label>
                <select
                  id="room-seat-room"
                  className="form-control"
                  value={roomSeatModal.salaId}
                  onChange={(event) => setRoomSeatModal((current) => (
                    current
                      ? { ...current, salaId: event.target.value }
                      : current
                  ))}
                  disabled={roomSeatSubmitting}
                >
                  {floors.filter((floor) => !usesTableMap(floor, legacyMapDefault)).map((floor) => {
                    const stats = floorStats.get(String(floor.id))
                      || {
                        total: Number(floor.capacidade_maxima) || 0,
                        occupied: 0,
                        available: Number(floor.capacidade_maxima) || 0,
                      };
                    return (
                      <option key={String(floor.id)} value={String(floor.id)}>
                        {floor.name} — {stats.available} cadeira{stats.available !== 1 ? 's' : ''} {stats.available === 1 ? 'disponível' : 'disponíveis'}
                      </option>
                    );
                  })}
                </select>
              </div>
              <div className="reservo-button-inline">
                <button
                  className="action-button"
                  type="submit"
                  disabled={roomSeatSubmitting || !roomSeatModal.salaId}
                >
                  <span>{roomSeatSubmitting ? 'Sentando...' : 'Sentar'}</span>
                </button>
                <button
                  className="action-button"
                  type="button"
                  onClick={() => setRoomSeatModal(null)}
                  disabled={roomSeatSubmitting}
                >
                  <span>Cancelar</span>
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {salaReleaseModal && createPortal(
        <div
          className="reservation-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="room-release-modal-title"
          onClick={(event) => {
            if (event.target === event.currentTarget && !salaReleaseSubmitting) {
              setSalaReleaseModal(null);
            }
          }}
        >
          <div className="reservation-modal-content reservation-modal-content-confirmation">
            <button
              type="button"
              className="reservation-modal-close"
              onClick={() => setSalaReleaseModal(null)}
              aria-label="Fechar liberação de cadeiras"
              disabled={salaReleaseSubmitting}
            >
              ×
            </button>
            <h2 id="room-release-modal-title" className="reservation-modal-title">
              Levantar pessoas
            </h2>
            <form
              className="reservation-modal-form"
              onSubmit={handleSalaReleaseSubmit}
            >
              <p className="reservation-modal-copy">
                <strong>
                  {salaReleaseModal.ocupacao.nome_cliente
                    || (
                      salaReleaseModal.ocupacao.reserva_id
                        ? reservationById.get(String(salaReleaseModal.ocupacao.reserva_id))?.name
                        : null
                    )
                    || 'Este grupo'}
                </strong>
                {' '}tem{' '}
                <strong>
                  {formatPeopleLabel(
                    Number(salaReleaseModal.ocupacao.numero_pessoas_presentes) || 0
                  )}
                </strong>
                {' '}no ambiente.
              </p>
              <div className="reservation-modal-room-select reservation-modal-release-quantity">
                <label htmlFor="room-release-quantity">
                  Quantas pessoas saíram?
                </label>
                <input
                  id="room-release-quantity"
                  className="form-control"
                  type="number"
                  min={1}
                  max={Number(salaReleaseModal.ocupacao.numero_pessoas_presentes) || 1}
                  value={salaReleaseModal.quantidadeSaida}
                  onChange={(event) => setSalaReleaseModal((current) => (
                    current
                      ? {
                        ...current,
                        quantidadeSaida: Number.parseInt(event.target.value, 10) || 0,
                      }
                      : current
                  ))}
                  disabled={salaReleaseSubmitting}
                  autoFocus
                />
                <small className="reservation-modal-hint">
                  Se sair apenas parte do grupo, as demais pessoas continuam ocupando cadeiras.
                </small>
              </div>
              <div className="reservo-button-inline">
                <button
                  className="action-button"
                  type="submit"
                  disabled={salaReleaseSubmitting}
                >
                  <span>{salaReleaseSubmitting ? 'Liberando...' : 'Liberar cadeiras'}</span>
                </button>
                <button
                  className="action-button"
                  type="button"
                  onClick={() => setSalaReleaseModal(null)}
                  disabled={salaReleaseSubmitting}
                >
                  <span>Cancelar</span>
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {noShowConfirmation && createPortal(
        <div
          className="reservation-modal-overlay"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="reservation-no-show-title"
          aria-describedby="reservation-no-show-description"
          onClick={(event) => {
            if (event.target === event.currentTarget && statusUpdatingId !== noShowConfirmation.reservationId) {
              setNoShowConfirmation(null);
            }
          }}
        >
          <div className="reservation-modal-content reservation-modal-content-confirmation">
            <button
              type="button"
              className="reservation-modal-close"
              onClick={() => setNoShowConfirmation(null)}
              aria-label="Fechar confirmação de não comparecimento"
              disabled={statusUpdatingId === noShowConfirmation.reservationId}
            >
              ×
            </button>
            <h2 id="reservation-no-show-title" className="reservation-modal-title">Confirmar não comparecimento</h2>
            <div className="reservation-modal-form">
              <p id="reservation-no-show-description" className="reservation-modal-copy">
                Confirmar que <strong>{noShowConfirmation.reservationName}</strong> não compareceu à reserva das {noShowConfirmation.reservationTime}?
              </p>
              <p className="reservation-modal-hint">
                Ao confirmar, a reserva terá o status alterado para <strong>no_show</strong> e será movida para a aba de finalizadas.
              </p>
              <div className="reservo-button-inline">
                <button
                  className="action-button"
                  type="button"
                  onClick={handleConfirmNoShow}
                  disabled={statusUpdatingId === noShowConfirmation.reservationId}
                >
                  <span>{statusUpdatingId === noShowConfirmation.reservationId ? 'Confirmando...' : 'Confirmar'}</span>
                </button>
                <button
                  className="action-button"
                  type="button"
                  onClick={() => setNoShowConfirmation(null)}
                  disabled={statusUpdatingId === noShowConfirmation.reservationId}
                >
                  <span>Cancelar</span>
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default DashboardModals;
